<template>
	<div class="p-fluid p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-6">
			<label for="documentClassId">{{ $t('Squeeze.XmlMapper.DocumentClass') }}</label>
			<Dropdown
				id="documentClassId"
				v-model="value.documentClassId"
				:options="documentClassOptions"
				optionValue="id"
				optionLabel="label"
				:filter="true"
				:autoFilterFocus="true"
				:showClear="true"
				:placeholder="$t('Squeeze.Prompts.Wildcard')"
				@change="onChangeClass"
				@show="showDropdownOverlay"
			/>
		</div>
		<div class="p-field p-col-6">
			<label for="company">{{ $t('Squeeze.XmlMapper.Company') }}</label>
			<Dropdown
				v-if="companyOptions.length"
				id="company"
				v-model="value.company"
				:options="companyOptions"
				optionValue="id"
				optionLabel="label"
				:filter="true"
				:autoFilterFocus="true"
				:showClear="true"
				:placeholder="$t('Squeeze.Prompts.Wildcard')"
				@change="onChangeCompany"
				@show="showDropdownOverlay"
			/>
			<InputText v-else id="company" v-model.trim="value.company" :placeholder="$t('Squeeze.Prompts.Wildcard')" @input="onChangeInput" />
		</div>
		<div class="p-field p-col-6">
			<label for="creditor">{{ $t('Squeeze.Prompts.Creditor') }}</label>
			<Dropdown
				v-if="creditorOptions.length"
				id="creditor"
				v-model="value.creditor"
				:options="filteredCreditorOptions"
				optionValue="id"
				optionLabel="label"
				:filter="true"
				:autoFilterFocus="true"
				:showClear="true"
				:placeholder="$t('Squeeze.Prompts.Wildcard')"
				@change="onChangeInput"
				@show="showDropdownOverlay"
			/>
			<InputText v-else id="creditor" v-model.trim="value.creditor" :placeholder="$t('Squeeze.Prompts.Wildcard')" @input="onChangeInput" />
		</div>
		<div class="p-field p-col-6">
			<label for="fieldName">{{ $t('Squeeze.XmlMapper.FieldName') }} *</label>
			<InputText v-if="readonlyFieldName" id="fieldName" v-model="value.fieldName" :disabled="true" />
			<Dropdown
				v-else
				id="fieldName"
				v-model="v$.fieldName.$model"
				:options="fields"
				optionValue="name"
				optionLabel="name"
				:filter="true"
				:filterFields="['name', 'translatedDescription', 'description']"
				:autoFilterFocus="true"
				:showClear="true"
				:class="{'p-invalid': v$.fieldName.$invalid && showErrorMessage}"
				@change="onChangeFieldName"
				@show="showDropdownOverlay"
			>
				<template #option="slotProps">
					<div>
						<span>{{ slotProps.option.name }}</span>
						<small v-if="getTranslatedDescription(slotProps.option)" class="field-option-description"> – {{ getTranslatedDescription(slotProps.option) }}</small>
					</div>
				</template>
			</Dropdown>
			<small v-if="!readonlyFieldName && v$.fieldName.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.XmlMapper.FieldName') }) }}
			</small>
		</div>
		<div class="p-field p-col-6">
			<label for="dataType">{{ $t('Squeeze.MasterData.FileType') }} *</label>
			<Dropdown
				id="dataType"
				v-model="v$.dataType.$model"
				:options="dataTypes"
				optionValue="id"
				optionLabel="text"
				:class="{'p-invalid': v$.dataType.$invalid && showErrorMessage}"
				@change="onChangeInput"
				@show="showDropdownOverlay"
			/>
			<small v-if="v$.dataType.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.MasterData.FileType') }) }}
			</small>
		</div>
		<div class="p-field p-col-12">
			<label for="prompt">{{ $t('Squeeze.Prompts.Prompt') }} *</label>
			<Textarea
				id="prompt"
				v-model.trim="v$.prompt.$model"
				:autoResize="true"
				rows="6"
				:class="{'p-invalid': v$.prompt.$invalid && showErrorMessage}"
				@input="onChangeInput"
			/>
			<small v-if="v$.prompt.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.Prompts.Prompt') }) }}
			</small>
		</div>
		<div class="p-field-checkbox p-col-12">
			<Checkbox id="active" v-model="value.active" :binary="true" @change="onChangeInput" />
			<label for="active">{{ $t('Squeeze.System.Active') }}</label>
		</div>
	</div>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {computed, defineComponent, nextTick, onMounted, PropType, reactive, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useVuelidate} from "@vuelidate/core";
import {required} from "@vuelidate/validators";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Checkbox from "primevue/checkbox";
import {DocumentClass, DocumentField, LlmPrompt, LlmPromptDataType, MasterDataCompanyEntry, MasterDataPartnerEntry} from "@dex/squeeze-client-ts";
import {showDropdownOverlay} from "@/util/StylesHelper";

export default defineComponent({
	name: "PromptForm",
	components: {
		Dropdown, InputText, Textarea, Checkbox,
	},
	props: {
		/** Entry to edit / create */
		promptEntry: {
			type: Object as PropType<LlmPrompt>,
			default: () => ({}),
			required: true,
		},
		/** LLM-enabled document classes */
		documentClasses: {
			type: Array as PropType<DocumentClass[]>,
			default: () => [],
		},
		/** Selectable fields (depending on the chosen document class) */
		fields: {
			type: Array as PropType<DocumentField[]>,
			default: () => [],
		},
		/** Master data options for the "Mandant" (company) field. Empty => free text input. */
		companyOptions: {
			type: Array as PropType<MasterDataCompanyEntry[]>,
			default: () => [],
		},
		/** Master data options for the "Geschäftspartner" (creditor) field. Empty => free text input. */
		creditorOptions: {
			type: Array as PropType<MasterDataPartnerEntry[]>,
			default: () => [],
		},
		/** Show validation error messages? */
		showErrorMessage: {
			type: Boolean,
			default: false,
		},
		/** Field context: the field name is fixed and shown read-only, document class is limited to the current class + wildcard */
		readonlyFieldName: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["update", "classChanged"],
	setup(props, {emit}) {
		const {t} = useI18n();

		/** Current object of all input fields */
		const value = reactive<LlmPrompt>(props.promptEntry);

		/** Validation rules (matching the required fields of the API) */
		const rules = {
			fieldName: {required},
			dataType: {required},
			prompt: {required},
		};

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value, {$scope: false});

		/**
		 * Document class options showing the translated description (fallback: description).
		 * A "Beliebig" (wildcard) option is always offered (also on /prompts). In field context
		 * only the current document class is passed in, so the list is limited to it + wildcard.
		 */
		const documentClassOptions = computed(() => {
			const options: {id: number | null | undefined; label: string | undefined}[] = props.documentClasses.map(documentClass => ({
				id: documentClass.id,
				label: getTranslatedDescription(documentClass),
			}));

			options.unshift({id: null, label: t('Squeeze.Prompts.Wildcard')});

			return options;
		});

		/**
		 * Creditor/debitor options filtered by the selected company.
		 * Creditors/debitors reference a company via companyId -> companies.code.
		 * No company selected (wildcard) => all creditors/debitors.
		 */
		const filteredCreditorOptions = computed(() => {
			if (!value.company) {
				return props.creditorOptions;
			}
			const company = props.companyOptions.find(entry => entry.id === value.company);
			if (!company || company.code == null) {
				return props.creditorOptions;
			}
			return props.creditorOptions.filter(entry => entry.companyId === company.code);
		});

		/** Available LLM data types */
		const dataTypes = computed(() => {
			return [
				{id: LlmPromptDataType.Str, text: t('Squeeze.Prompts.DataTypes.Str')},
				{id: LlmPromptDataType.Int, text: t('Squeeze.Prompts.DataTypes.Int')},
				{id: LlmPromptDataType.Float, text: t('Squeeze.Prompts.DataTypes.Float')},
				{id: LlmPromptDataType.Bool, text: t('Squeeze.Prompts.DataTypes.Bool')},
				{id: LlmPromptDataType.Date, text: t('Squeeze.Prompts.DataTypes.Date')},
				{id: LlmPromptDataType.List, text: t('Squeeze.Prompts.DataTypes.List')},
			];
		});

		/**  Emit the current (validated) value. */
		const emitUpdate = async () => {
			await nextTick();
			emit("update", value, v$.value.$invalid);
		};

		/** Triggered on change of any plain input field */
		const onChangeInput = () => {
			emitUpdate();
		};

		/** Reset the creditor when it no longer belongs to the newly selected company, then emit */
		const onChangeCompany = () => {
			if (value.creditor && !filteredCreditorOptions.value.some(entry => entry.id === value.creditor)) {
				value.creditor = undefined;
			}
			emitUpdate();
		};

		/** Triggered when a batch- or document class changes (reloads the field list) */
		const onChangeClass = () => {
			emit("classChanged", value.batchClassId ?? null, value.documentClassId ?? null);
			emitUpdate();
		};

		/** The field name the current prompt belongs to (used to detect an unchanged default prompt) */
		let previousFieldName: string | undefined = value.fieldName;

		/** Returns the description (= default prompt) of the field with the given name */
		const descriptionOfField = (fieldName?: string): string => {
			const field = props.fields.find(entry => entry.name === fieldName);
			return field?.description ?? "";
		};

		/**
		 * Triggered when the field name changes.
		 * Sets the prompt to the new field's description (default prompt) - even if the prompt is
		 * not empty - but only as long as the prompt still holds the previous field's default
		 * description. If the user customized the prompt, it is kept untouched.
		 */
		const onChangeFieldName = () => {
			const previousDescription = descriptionOfField(previousFieldName);
			if (!value.prompt || value.prompt === previousDescription) {
				value.prompt = descriptionOfField(value.fieldName);
			}
			previousFieldName = value.fieldName;
			emitUpdate();
		};

		onMounted(async () => {
			Object.assign(value, props.promptEntry);
			previousFieldName = value.fieldName;
			await v$.value.$validate();
			emitUpdate();
		});

		/** Keep local value in sync when the parent swaps the entry */
		watch(() => props.promptEntry, () => {
			Object.assign(value, props.promptEntry);
			previousFieldName = value.fieldName;
		});

		return {
			getTranslatedDescription,
			t,
			value,
			v$,
			dataTypes,
			documentClassOptions,
			filteredCreditorOptions,
			onChangeInput,
			onChangeCompany,
			onChangeClass,
			onChangeFieldName,
			showDropdownOverlay,
		};
	},
});
</script>

<style scoped>
.p-grid {
	margin-left: 0;
	margin-right: 0;
}

.field-option-description {
	color: var(--text-color-secondary);
}
</style>
